import { useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useNavigationLinks from "@hooks/useNavigationLinks"
import useRouter from "@hooks/useRouter"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import filterUserLinks from "@utils/filterUserLinks"

import { AccessCheck } from "@particles"

import { Button } from "@atoms"

import { NavTopBar } from "@organisms"

import { AppFrame } from "@templates"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import ReportDashboard from "./ReportDashboard/ReportDashboard"
import styles from "./Reports.module.scss"
import { useReportDashboardsQuery } from "./Reports.store"

function ReportsContent() {
    const isDesktop = useIsDesktop()

    const { user } = useUser()
    const { push } = useRouter()
    const { reports: reportsLink } = useNavigationLinks()

    const { data: reportDashboards = [] } = useReportDashboardsQuery(user)
    const [isAddReportDialogOpen, setIsAddReportDialogOpen] = useState(false)

    const sublinks = filterUserLinks(reportsLink.subLinks, user)

    // Get report query param from URL
    let reportParam = new URLSearchParams(window.location.search).get("report")

    // If no report param and we have navigation items, default to first one
    if (!reportParam && sublinks.length > 0) {
        // Extract report ID from the first sublink path
        const match = sublinks[0].path.match(/report=(\d+)/)
        if (match) {
            reportParam = match[1]
            // Update URL without reload
            const url = new URL(window.location.href)
            url.searchParams.set("report", reportParam)
            window.history.replaceState({}, "", url)
        }
    }

    // Find the active dashboard based on reportParam
    const activeDashboard = reportParam ? reportDashboards.find((d) => d.id.toString() === reportParam) : null

    if (!user) {
        return null
    }

    return (
        <>
            <NavTopBar.Root>
                <NavTopBar.Header>
                    <NavTopBar.NavTopBarHeaderStart>
                        <NavTopBar.PageTitle
                            label={activeDashboard?.name ?? "Reports"}
                            onNavigationItemSelect={
                                activeDashboard && sublinks.length > 1 ? (item) => push(item.value) : undefined
                            }
                            navigationItems={
                                activeDashboard && sublinks.length > 1
                                    ? sublinks.map((item) => ({
                                          label: item.label,
                                          value: item.path,
                                          isActive: item.path.includes(`report=${reportParam}`),
                                      }))
                                    : undefined
                            }
                        />
                    </NavTopBar.NavTopBarHeaderStart>
                    {activeDashboard && isDesktop && (
                        <NavTopBar.NavTopBarHeaderEnd>
                            <AccessCheck
                                permissions={{
                                    reports_edit_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <Button
                                    size={isDesktop ? "md" : "lg"}
                                    variant="solid"
                                    leftIcon={faIcon({ name: "plus", style: "solid" })}
                                    onClick={() => setIsAddReportDialogOpen(true)}
                                    noDebounce={true}
                                >
                                    {isDesktop ? "New" : null}
                                </Button>
                            </AccessCheck>
                        </NavTopBar.NavTopBarHeaderEnd>
                    )}
                </NavTopBar.Header>
            </NavTopBar.Root>
            <AppFrame.PageContent>
                {activeDashboard ? (
                    <ReportDashboard
                        dashboardId={activeDashboard.dashboard_id}
                        isAddReportDialogOpen={isAddReportDialogOpen}
                        onAddReportDialogChange={setIsAddReportDialogOpen}
                    />
                ) : (
                    <div className={styles.emptyMessage}>No report dashboards are available.</div>
                )}
            </AppFrame.PageContent>
        </>
    )
}

export default function Reports() {
    return (
        <AppFrame.Root>
            <ReportsContent />
        </AppFrame.Root>
    )
}
